/**
 * The menu bar, like Unreal Engine's: the logo, the menus (File, Edit, View, Window, Tools,
 * Help; their lines come from the services, `state/shell.ts`), then on the right where you
 * work: the project's name (a dot when unsaved), who else is here, the server when working
 * online in a remote space, the way to your teams and spaces (the server's app pages for a page
 * it served, else the Spaces window), and your role in a space (opens the Share window).
 *
 * A menu opens below its title on a click; while one is open, moving over another title opens
 * that one instead, and the left and right arrows go to the neighbouring menus.
 */
import { For, Show, type JSX } from "solid-js";
import { useApp } from "./context";
import { People } from "./People";
import { Icon } from "../shared/icons";
import type { MenuBarId } from "../shared/menus";

/** The menu bar. */
export function MenuBar(): JSX.Element {
  const { project, wm, access, spaces, menu, shell, palette, keys } = useApp();
  /** The palette's shortcut, for the search button's tooltip. */
  const paletteKeys = () => keys.shortcut("palette.open");
  /** The menu bar's menu open now, if any. */
  const openMenu = () => menu.state()?.owner ?? null;
  /** The title buttons, to place a menu under its title. */
  const titles = new Map<string, HTMLButtonElement>();

  /** Opens menu `id` below its title (closing the one open). */
  const show = (id: MenuBarId) => {
    const title = titles.get(id);
    if (!title) return;
    const r = title.getBoundingClientRect();
    menu.openAt(r.left, r.bottom + 2, shell.menu(id), id);
  };
  /** The menu `step` places away from `id` (wrapping round). */
  const neighbour = (id: string, step: number): MenuBarId => {
    const ids = shell.menus().map((m) => m.id);
    return ids[(ids.indexOf(id as MenuBarId) + step + ids.length) % ids.length]!;
  };
  menu.setNeighbours((id, step) => show(neighbour(id, step)));

  return (
    <header class="menubar" id="menubar">
      <div class="brand">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
          <circle cx="6" cy="7" r="2.4" />
          <circle cx="18" cy="6" r="2.4" />
          <circle cx="12" cy="18" r="2.4" />
          <path d="M8 8l3 8M16.4 8L13 16M8.4 7h7.2" />
        </svg>
        <h1>Knodal</h1>
      </div>
      <nav class="mb-menus" role="menubar" aria-label="Menus">
        <For each={shell.menus()}>
          {(m) => (
            <button
              type="button"
              class="mb-title"
              role="menuitem"
              data-menubar={m.id}
              aria-haspopup="menu"
              aria-expanded={openMenu() === m.id}
              ref={(el) => titles.set(m.id, el)}
              onClick={() => (openMenu() === m.id ? menu.close() : show(m.id))}
              onPointerEnter={() => openMenu() && openMenu() !== m.id && show(m.id)}
            >
              {m.label}
            </button>
          )}
        </For>
      </nav>
      <button type="button" class="mb-search" id="palette-open" title={`Find an action, a window or a node${paletteKeys() ? ` (${paletteKeys()})` : ""}`} onClick={() => palette.show()}>
        <Icon name="search" size={14} />
        <span>Find…</span>
        <Show when={paletteKeys()}>
          <kbd>{paletteKeys()}</kbd>
        </Show>
      </button>
      <div class="mb-right">
        <button type="button" class="projname" id="project-title" data-dirty={project.dirty() ? "yes" : "no"} title={project.state()?.path ?? "not saved yet"} onClick={() => wm.open("project")}>
          {project.state()?.name ?? "…"}
          <Show when={project.dirty()}>
            <i class="dirty-dot" title="Unsaved changes" />
          </Show>
        </button>
        <People />
        <Show when={spaces.remote && spaces.current}>
          {(remote) => (
            <button type="button" class="btn remote-chip" id="remote-chip" data-server={remote().server} title="Working online in a space on this server (your own board stays on this computer)" onClick={() => wm.open("home")}>
              Online · {new URL(remote().server).host}
            </button>
          )}
        </Show>
        <Show
          when={spaces.served && !spaces.remote}
          fallback={
            <button type="button" class="btn" id="spaces-open" title="Your teams and their spaces, on the servers you are signed in to" onClick={() => wm.open("home")}>
              Spaces
            </button>
          }
        >
          <a class="btn" id="to-dashboard" href="/" title="Your teams and their spaces on this server">
            Your spaces
          </a>
        </Show>
        <Show when={access.shared()}>
          <button type="button" class="btn primary" id="share-open" data-role={access.role() ?? "none"} title="Who is in this space and what they may do" onClick={() => wm.open("share")}>
            Share · {access.role() ?? "no role"}
          </button>
        </Show>
      </div>
    </header>
  );
}
